import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_pipelines: number
  active_pipelines: number
  executions_today: number
  executions_mtd: number
  records_processed_today: number
  avg_execution_time_sec: number
}

interface Pipeline {
  id: string
  name: string
  source: string
  destination: string
  schedule: string
  last_run: string
  status: string
}

interface Component {
  type: string
  name: string
  description: string
  usage_count: number
}

export default function ETLBuilderPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [pipelines, setPipelines] = useState<Pipeline[]>([])
  const [components, setComponents] = useState<Component[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, pipelinesRes, componentsRes] = await Promise.all([
          fetch('/api/etlbuilder/statistics'),
          fetch('/api/etlbuilder/pipelines'),
          fetch('/api/etlbuilder/components')
        ])
        setStatistics(await statsRes.json())
        setPipelines(await pipelinesRes.json())
        setComponents(await componentsRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      'Active': 'bg-green-100 text-green-800',
      'Running': 'bg-blue-100 text-blue-800',
      'Paused': 'bg-yellow-100 text-yellow-800',
      'Failed': 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading ETL Builder...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">ETL Pipeline Builder</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.total_pipelines}</div>
              <div className="text-sm text-gray-500">Total Pipelines</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.active_pipelines}</div>
              <div className="text-sm text-gray-500">Active</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{statistics.executions_today.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Executions Today</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-indigo-600">{(statistics.executions_mtd / 1000).toFixed(0)}K</div>
              <div className="text-sm text-gray-500">Executions MTD</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">{(statistics.records_processed_today / 1000000).toFixed(1)}M</div>
              <div className="text-sm text-gray-500">Records Today</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-teal-600">{statistics.avg_execution_time_sec}s</div>
              <div className="text-sm text-gray-500">Avg Execution</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">ETL Pipelines</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Pipeline</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Destination</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Schedule</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {pipelines.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{p.name}</td>
                      <td className="px-4 py-3 text-gray-600">{p.source}</td>
                      <td className="px-4 py-3 text-gray-600">{p.destination}</td>
                      <td className="px-4 py-3 text-gray-600">{p.schedule}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(p.status)}`}>
                          {p.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Components</h2></div>
            <div className="p-4 space-y-3">
              {components.map((comp, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{comp.name}</span>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{comp.type}</span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">{comp.description}</div>
                  <div className="text-xs text-gray-400 mt-1">{comp.usage_count} uses</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
